import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_DESCRIPTION =
  "Receive the full exhibition catalogue — featuring works by Guilherme Albagli and Ulviyya Akhundova, dimensions, mediums and curatorial notes — sent directly to your inbox.";

export default function CatalogueRequest({
  project = "shape-of-elsewhere",
  description = DEFAULT_DESCRIPTION,
  fileNote = "PDF · ~8 MB",
}) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/catalogue-requests`, { ...form, project });
      setSent(true);
      setForm({ name: "", email: "" });
      toast.success(data?.message || "Request received.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="request-catalogue"
      data-testid="catalogue-request-section"
      className="bg-nyg-cream py-24 md:py-40 border-t border-nyg-dark/10"
    >
      <Toaster position="bottom-center" richColors />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-5"
          >
            <span className="label">For Collectors &amp; Press</span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-nyg-dark mt-6">
              Request the
              <br />
              <span className="italic font-light">catalogue.</span>
            </h2>
            <p className="font-body font-light text-base md:text-lg text-nyg-dark/70 mt-8 max-w-md leading-relaxed">
              {description}
            </p>
            <div className="mt-10 flex items-center gap-3 text-nyg-dark/60">
              <Download size={16} strokeWidth={1.2} />
              <span className="label !text-nyg-dark/60">{fileNote}</span>
            </div>
          </motion.div>

          {/* Right - form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, delay: 0.15 }}
            onSubmit={onSubmit}
            data-testid="catalogue-request-form"
            className="col-span-12 md:col-span-6 md:col-start-7 mt-4 md:mt-12"
          >
            <div className="space-y-10">
              <div>
                <label
                  className="label !text-nyg-dark/60 block mb-3"
                  htmlFor="cat-name"
                >
                  Name
                </label>
                <input
                  id="cat-name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={onChange}
                  data-testid="catalogue-name-input"
                  className="w-full bg-transparent border-0 border-b border-nyg-dark/30 focus:border-nyg-dark outline-none px-0 py-3 text-nyg-dark text-lg font-light placeholder-nyg-dark/30 transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label
                  className="label !text-nyg-dark/60 block mb-3"
                  htmlFor="cat-email"
                >
                  Email
                </label>
                <input
                  id="cat-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  data-testid="catalogue-email-input"
                  className="w-full bg-transparent border-0 border-b border-nyg-dark/30 focus:border-nyg-dark outline-none px-0 py-3 text-nyg-dark text-lg font-light placeholder-nyg-dark/30 transition-colors"
                  placeholder="you@email.com"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="catalogue-submit-btn"
                className="group inline-flex items-center gap-3 bg-nyg-dark text-nyg-cream px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-nyg-dark/85 transition-all disabled:opacity-50"
              >
                {submitting
                  ? "Sending..."
                  : sent
                    ? "Request Another"
                    : "Send Me the Catalogue"}
                <ArrowUpRight
                  size={16}
                  strokeWidth={1}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>

              {sent && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="font-body italic text-sm text-nyg-dark/70"
                  data-testid="catalogue-success-message"
                >
                  Thank you — the catalogue has been sent to your inbox.
                </motion.p>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
